import React from 'react';
import { useHistory } from 'react-router-dom';
import { Bot } from 'lucide-react';
import { Button } from '@components/atoms/Button';
import { useAssistantAvailable, useResumeProjectConversation } from '@hooks/api/assistant';
import { useFeatureAccess } from '@utils/featureFlag';
import { useTranslation } from '@hooks/useTranslation';

interface AssistantProjectButtonProps {
  projectId?: string;
}

/**
 * Sprint 37, story-199. Project detail header action. On click it resolves the user's latest
 * conversation for the project: resume it (`/assistant/<id>`) or open an empty project-bound chat
 * (`/assistant?project=<id>`). Hidden unless the role may use the assistant, the assistant is
 * available, and the project came from the role-scoped project query (the parent only renders it
 * with a loaded project).
 *
 * Walkthrough fix (sprint 38): icon only (the same `Bot` glyph mobile's ProjectDetail uses), with
 * the translated "Ask the assistant about this project" as its tooltip and accessible name, and
 * only on the project detail view — the text label did not fit the card header, and the schedule
 * header entry was removed.
 */
export const AssistantProjectButton: React.FC<AssistantProjectButtonProps> = ({ projectId }) => {
  const t = useTranslation('assistant');
  const history = useHistory();
  const { canAccess } = useFeatureAccess();
  const { data: available } = useAssistantAvailable();
  const { resolveRoute, isResolving } = useResumeProjectConversation();

  if (!projectId || available !== true || !canAccess('ASSISTANT')) return null;

  return (
    <Button
      type="button"
      variant="tertiary"
      onlyIcon
      disabled={isResolving}
      title={t.askAssistantTooltip}
      aria-label={t.askAssistantTooltip}
      onClick={async () => history.push(await resolveRoute(projectId))}
      data-testid="assistant-project-button">
      <Bot size={16} aria-hidden="true" />
    </Button>
  );
};

export default AssistantProjectButton;
